<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { frontendURL } from 'dashboard/helper/URLHelper';
import ConversationApi from 'dashboard/api/inbox/conversation';
import {
  CONVERSATION_PRIORITY,
  CONVERSATION_PRIORITY_ORDER,
} from 'shared/constants/messages';
import NextButton from 'dashboard/components-next/button/Button.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import CardLayout from 'dashboard/components-next/CardLayout.vue';

const props = defineProps({
  conversations: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['cleared']);

const { t } = useI18n();
const router = useRouter();
const { accountId } = useAccount();

// Urgente primero, baja al final — mismo orden que el resto del producto
// (CONVERSATION_PRIORITY_ORDER, shared/constants/messages.js).
const PRIORITY_LEVELS = Object.values(CONVERSATION_PRIORITY).sort(
  (a, b) => CONVERSATION_PRIORITY_ORDER[b] - CONVERSATION_PRIORITY_ORDER[a]
);

const grouped = computed(() =>
  PRIORITY_LEVELS.map(level => ({
    level,
    items: props.conversations.filter(
      conversation => conversation.priority === level
    ),
  })).filter(group => group.items.length)
);

const priorityLabel = level =>
  t(`CONVERSATION.PRIORITY.OPTIONS.${level.toUpperCase()}`);

const openConversation = conversation => {
  router.push(
    frontendURL(`accounts/${accountId.value}/conversations/${conversation.id}`)
  );
};

const clearingId = ref(null);

const clearPriority = async conversation => {
  clearingId.value = conversation.id;
  try {
    // Se llama a la API directo (en vez del action `assignPriority` del
    // store de conversations) porque ese action absorbe cualquier error
    // silenciosamente — acá si el pedido falla de verdad necesitamos
    // enterarnos, para no sacar el caso de la vista sin haber limpiado la
    // prioridad en el backend.
    await ConversationApi.togglePriority({
      conversationId: conversation.id,
      priority: null,
    });
    emit('cleared', conversation.id);
  } catch (error) {
    useAlert(t('SICARG_DASHBOARD.PRIORITY.REMOVE_ERROR'));
  } finally {
    clearingId.value = null;
  }
};
</script>

<template>
  <CardLayout>
    <h2 class="text-sm font-medium text-n-slate-12">
      {{ t('SICARG_DASHBOARD.PRIORITY.TITLE') }}
    </h2>
    <p
      v-if="!conversations.length"
      class="p-6 text-sm text-center rounded-lg border text-n-slate-10 border-n-weak"
    >
      {{ t('SICARG_DASHBOARD.PRIORITY.EMPTY') }}
    </p>
    <div
      v-else
      class="flex overflow-y-auto flex-col gap-3 pr-1 max-h-[420px]"
    >
      <div v-for="group in grouped" :key="group.level" class="flex flex-col gap-1">
        <h3
          class="flex gap-1.5 items-center text-xs font-medium tracking-wide uppercase text-n-slate-10"
        >
          <CardPriorityIcon :priority="group.level" />
          {{ priorityLabel(group.level) }}
          <span class="normal-case text-n-slate-9">({{ group.items.length }})</span>
        </h3>
        <div class="rounded-lg border divide-y border-n-weak divide-n-weak">
          <div
            v-for="conversation in group.items"
            :key="conversation.id"
            class="flex justify-between items-center px-4 py-3"
          >
            <button
              class="flex flex-col flex-grow text-left"
              @click="openConversation(conversation)"
            >
              <span class="text-sm font-medium text-n-slate-12">
                {{ conversation.contact?.name || t('SICARG_DASHBOARD.LIST.NO_NAME') }}
                <span class="ml-1 text-xs text-n-slate-10"
                  >#{{ conversation.id }}</span
                >
                <span
                  v-if="conversation.status === 'resolved'"
                  class="px-1.5 py-0.5 ml-1 text-xs rounded-full bg-n-teal-3 text-n-teal-11"
                >
                  {{ t('SICARG_DASHBOARD.PRIORITY.RESOLVED') }}
                </span>
              </span>
              <span
                v-if="conversation.numero_cuenta"
                class="text-xs text-n-slate-11"
              >
                {{ t('SICARG_DASHBOARD.LIST.ACCOUNT') }}:
                {{ conversation.numero_cuenta }}
              </span>
            </button>
            <NextButton
              faded
              slate
              sm
              icon="i-lucide-x"
              :is-loading="clearingId === conversation.id"
              :label="t('SICARG_DASHBOARD.PRIORITY.REMOVE')"
              @click="clearPriority(conversation)"
            />
          </div>
        </div>
      </div>
    </div>
  </CardLayout>
</template>
